






























import { computed, defineComponent, onBeforeUnmount, ref } from "@vue/composition-api";
import { throttle } from "lodash-es";
import { Location } from "vue-router";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { MediaFilesController } from "@/lib/MediaFilesController";
import { getProfileUrl } from "@/lib/urlHelpers";
import { EVENTS } from "@/const";
import BaseAvatar from "@/components/BaseAvatar";
import BaseIcon from "@/components/BaseIcon";

interface MenuItem {
  label: string;
  active?: string;
  name: string;
  to: Location;
  hasNew?: boolean;
  exact?: boolean;
  class?: string;
}

export default defineComponent({
  name: "MainNavigationBar",
  components: { BaseIcon, BaseAvatar },
  setup() {
    const { authUserStrict } = useAuthUser();
    const unreadMessageThreadsCount = ref(0);
    const unreadNotificationsCount = ref(0);

    const profileTo = computed(() => getProfileUrl(authUserStrict.value));

    const avatarUrl = computed(() => {
      if (!authUserStrict.value.avatar) return "";
      return MediaFilesController.getImageThumbnail(authUserStrict.value.avatar.urlPart, 76);
    });

    const hasNewMessages = computed(() => unreadMessageThreadsCount.value > 0);
    const hasNewNotifications = computed(() => unreadNotificationsCount.value > 0);

    const menuList = computed<MenuItem[]>(() => {
      if (!authUserStrict.value) return [];
      return [
        {
          label: "sci-home-outline",
          active: "sci-home",
          name: $t("Home"),
          to: {
            name: "home",
          },
        },
        {
          label: "sci-bell-outline",
          active: "sci-bell",
          name: $t("Notifications"),
          to: {
            name: "notifications",
          },
          hasNew: hasNewNotifications.value,
        },
        {
          label: "sci-search",
          active: "sci-search",
          name: $t("Discover"),
          to: {
            name: "discover",
          },
        },
        {
          label: "sci-chat-outline",
          active: "sci-chat",
          name: $t("Messages"),
          to: {
            name: "messageThreads",
          },
          hasNew: hasNewMessages.value,
        },
        {
          label: "sci-image-multiple-outline",
          active: "sci-image-multiple",
          name: $t("Purchases"),
          to: {
            name: "purchases",
          },
          class: "__mobile-hidden",
        },
        {
          label: "sci-account-circle-outline",
          active: "sci-account-circle",
          name: $t("Profile"),
          to: profileTo.value,
          exact: true,
          class: "__mobile-hidden",
        },
        {
          label: "sci-menu",
          name: $t("More"),
          to: {
            name: "more",
          },
          class: "__mobile-hidden",
        },
        {
          label: "sci-account-circle-outline",
          active: "sci-account-circle",
          name: $t("Mobile More"),
          to: {
            name: "more",
          },
          class: "__mobile-only",
        },
      ];
    });

    const getUnreadMessagesCount = async () => {
      unreadMessageThreadsCount.value = await providers.MessageThreadsProvider.getUnreadCount();
    };

    const getUnreadNotificationsCount = async () => {
      const unreadNotificationsStats = await providers.NotificationsProvider.getUnreadStats();
      if (unreadNotificationsStats) unreadNotificationsCount.value = unreadNotificationsStats.count;
    };

    const receivedMessage = throttle(getUnreadMessagesCount, 3000, { trailing: false });
    const receivedNotification = throttle(getUnreadNotificationsCount, 3000, { trailing: false });

    const hideMenuHandler = (hide: boolean = false) => {
      const root = document.documentElement;
      root.style.setProperty("--safe-bottom-position-nav", hide ? "0px" : "56px");
    };

    const created = async () => {
      hideMenuHandler();
      await Promise.all([getUnreadMessagesCount(), getUnreadNotificationsCount()]);
    };

    created();
    emitter.on<any>(EVENTS.MESSAGES_NEW_MESSAGE, receivedMessage);
    emitter.on<any>(EVENTS.MESSAGES_MARKED_READ, getUnreadMessagesCount);
    emitter.on<any>(EVENTS.NOTIFICATIONS_NEW_NOTIFICATION, receivedNotification);
    emitter.on<any>(EVENTS.NOTIFICATIONS_MARKED_READ, getUnreadNotificationsCount);
    emitter.on<any>(EVENTS.MAIN_MENU_HIDE, hideMenuHandler);

    onBeforeUnmount(() => {
      hideMenuHandler(true);
      emitter.off<any>(EVENTS.MESSAGES_NEW_MESSAGE, receivedMessage);
      emitter.off<any>(EVENTS.MESSAGES_MARKED_READ, getUnreadMessagesCount);
      emitter.off<any>(EVENTS.NOTIFICATIONS_NEW_NOTIFICATION, receivedNotification);
      emitter.off<any>(EVENTS.NOTIFICATIONS_MARKED_READ, getUnreadNotificationsCount);
      emitter.off<any>(EVENTS.MAIN_MENU_HIDE, hideMenuHandler);
    });

    return { avatarUrl, menuList, authUserStrict, profileTo };
  },
});
